import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export interface CountryDistance {
  fromCountry: string
  toCountry: string
  distanceKm: number
  cardinalDirection: string
  azimuthDegrees: number
  fromPointLon: number
  fromPointLat: number
  toPointLon: number
  toPointLat: number
  fromCenterLon: number
  fromCenterLat: number
  toCenterLon: number
  toCenterLat: number
  centroidLoxoBearings?: [number, number]
  rhumbLineBearings?: [number, number]
  rhumbLineBearing?: number
}

export interface OptimizedCountryPair {
  countries: [string, string]
  distance: number
  bearings: [number, number] // [country1→country2, country2→country1]
  directions: [string, string] // [country1→country2, country2→country1]
  borderPoints: [[number, number], [number, number]] // [point1, point2] - closest border points
  centroids: [[number, number], [number, number]] // [centroid1, centroid2] - country centroids
  rhumbLineBearings: [number, number] // [country1→country2, country2→country1] rhumb line bearings
}

export interface OptimizedDistanceData {
  pairs: OptimizedCountryPair[]
}

export interface CountryFeature {
  type: 'Feature'
  geometry: {
    type: 'MultiPolygon' | 'Polygon'
    coordinates: number[][][] | number[][][][]
  }
  properties: {
    NAME?: string
    ISO_A2?: string
    [key: string]: any
  }
}

export interface Centroid {
  country: string
  latitude: number
  longitude: number
}

export interface CountryData {
  type: 'FeatureCollection'
  features: CountryFeature[]
}

export function parseCSVLine(line: string): CountryDistance | null {
  // Handle quoted CSV values properly
  const parts: string[] = []
  let current = ''
  let inQuotes = false
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    if (char === '"') {
      inQuotes = !inQuotes
    } else if (char === ',' && !inQuotes) {
      parts.push(current.trim())
      current = ''
    } else {
      current += char
    }
  }
  parts.push(current.trim())
  
  if (parts.length !== 13) return null
  
  const result = {
    fromCountry: parts[0],
    toCountry: parts[1],
    distanceKm: parseFloat(parts[2]),
    cardinalDirection: parts[3],
    azimuthDegrees: parseFloat(parts[4]),
    fromPointLon: parseFloat(parts[5]),
    fromPointLat: parseFloat(parts[6]),
    toPointLon: parseFloat(parts[7]),
    toPointLat: parseFloat(parts[8]),
    fromCenterLon: parseFloat(parts[9]),
    fromCenterLat: parseFloat(parts[10]),
    toCenterLon: parseFloat(parts[11]),
    toCenterLat: parseFloat(parts[12])
  }
  
  // Validate that numeric fields parsed correctly
  if (isNaN(result.distanceKm) || isNaN(result.azimuthDegrees) || 
      isNaN(result.fromPointLon) || isNaN(result.fromPointLat) ||
      isNaN(result.toPointLon) || isNaN(result.toPointLat) ||
      isNaN(result.fromCenterLon) || isNaN(result.fromCenterLat) ||
      isNaN(result.toCenterLon) || isNaN(result.toCenterLat)) {
    return null
  }
  
  return result
}

export function findDistanceBetweenCountries(
  distances: CountryDistance[],
  country1: string,
  country2: string
): CountryDistance | null {
  return distances.find(
    (d) =>
      (d.fromCountry === country1 && d.toCountry === country2) ||
      (d.fromCountry === country2 && d.toCountry === country1)
  ) ?? null
}

export function parseOptimizedDistanceData(data: any[]): OptimizedCountryPair[] {
  // Skip header row and parse optimized format
  return data.slice(1).map(row => ({
    countries: row[0] as [string, string],
    distance: row[1] as number,
    bearings: row[2] as [number, number],
    directions: row[3] as [string, string],
    borderPoints: row[4] as [[number, number], [number, number]],
    centroids: row[5] as [[number, number], [number, number]],
    rhumbLineBearings: row[6] as [number, number]
  }))
}

export function findOptimizedDistanceBetweenCountries(
  pairs: OptimizedCountryPair[],
  country1: string,
  country2: string
): { distance: number; bearing: number; direction: string; borderPoints: [[number, number], [number, number]]; centroids: [[number, number], [number, number]]; rhumbLineBearings: [number, number]; rhumbLineBearing: number } | null {
  const pair = pairs.find(p => 
    (p.countries[0] === country1 && p.countries[1] === country2) ||
    (p.countries[0] === country2 && p.countries[1] === country1)
  )
  
  if (!pair) return null
  
  // Determine which direction we need
  const isForward = pair.countries[0] === country1
  const bearingIndex = isForward ? 0 : 1
  const directionIndex = isForward ? 0 : 1
  const rhumbBearingIndex = isForward ? 0 : 1
  
  return {
    distance: pair.distance,
    bearing: pair.bearings[bearingIndex],
    direction: pair.directions[directionIndex],
    borderPoints: pair.borderPoints,
    centroids: pair.centroids,
    rhumbLineBearings: pair.rhumbLineBearings,
    rhumbLineBearing: pair.rhumbLineBearings[rhumbBearingIndex]
  }
}

export function formatDistance(km: number): string {
  return `${km.toFixed(1)} km`;
}

/**
 * Calculate Haversine distance between two points in km.
 * This is the V1 centroid-to-centroid distance calculation.
 */
export function haversineDistance(
  lat1: number, lon1: number,
  lat2: number, lon2: number
): number {
  const R = 6371; // Earth radius in km
  const toRad = (deg: number) => deg * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Calculate initial bearing (great circle) from point 1 to point 2.
 * Returns degrees 0-360 (0=N, 90=E, 180=S, 270=W).
 * This is the V1 direction calculation.
 */
export function initialBearing(
  lat1: number, lon1: number,
  lat2: number, lon2: number
): number {
  const toRad = (deg: number) => deg * Math.PI / 180;
  const toDeg = (rad: number) => rad * 180 / Math.PI;
  const dLon = toRad(lon2 - lon1);
  const y = Math.sin(dLon) * Math.cos(toRad(lat2));
  const x =
    Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLon);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

/**
 * Convert bearing to cardinal direction
 * @param bearing - Degrees from North (0°-360°)
 * @returns Compass direction (e.g., "N", "NE")
 */
export function bearingToCardinal(bearing: number): string {
  if (isNaN(bearing)) return "N/A";
  
  const directions = [
    "N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE",
    "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"
  ];
  
  return directions[Math.round(bearing / 22.5) % 16];
}

export const distanceToWarmthText = (distance: number): string => {
  if (distance === 0) {
    return "Borders 🔥🔥🔥";
  } else if (distance < 500) {
    return "Very Hot 🔥🔥";
  } else if (distance < 1000) {
    return "Hot 🔥";
  } else if (distance < 3000) {
    return "Warm";
  } else if (distance < 5000) {
    return "Cool";
  } else if (distance < 8000) {
    return "Cold ❄️";
  } else {
    return "Ice Cold ❄️❄️";
  }
};
